import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import RunTelemetryPanel from '../components/RunTelemetryPanel';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { formatRunDuration, isRunActive, runContextLabel, runStatusColor } from '../utils/assetRuns';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';


const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};
const REFRESH_MS = 15000;

function label(value) {
  return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function date(value) {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function bytes(value) {
  const size = Number(value);
  if (!Number.isFinite(size)) return 'N/A';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function deliveryColor(status) {
  const value = String(status || '').toLowerCase();
  if (value === 'accepted') return 'success';
  if (['rejected', 'publish_failed'].includes(value)) return 'error';
  if (value === 'timed_out') return 'warning';
  if (['preparing', 'published'].includes(value)) return 'info';
  return 'default';
}

function JsonBlock({ value, emptyMessage }) {
  const normalized = value && typeof value === 'object' ? value : {};
  if (!Object.keys(normalized).length) {
    return <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>;
  }
  return (
    <Box
      component="pre"
      sx={{ m: 0, p: 1.5, borderRadius: 1, bgcolor: 'background.default', overflowX: 'auto', fontSize: 13 }}
    >
      {JSON.stringify(normalized, null, 2)}
    </Box>
  );
}

export default function AssetRunDetailPage(props) {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [state, setState] = useState({ loading: true, error: '', run: null, telemetry: null });
  const [downloadError, setDownloadError] = useState('');
  const [downloading, setDownloading] = useState('');
  const [commandAction, setCommandAction] = useState({ busy: false, error: '', success: '' });

  const load = useCallback(async ({ background = false } = {}) => {
    if (!background) setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const telemetryRequest = axiosInstance
        .get(`/runs/${encodeURIComponent(runId)}/telemetry`, { params: { limit: 10000 } })
        .then((response) => response.data?.telemetry)
        .catch((error) => ({
          configured: false,
          message: error?.response?.data?.message || 'Telemetry is temporarily unavailable for this Run.',
          rows: [],
          fields: [],
        }));
      const [detailResponse, telemetry] = await Promise.all([
        axiosInstance.get(`/runs/${encodeURIComponent(runId)}`),
        telemetryRequest,
      ]);
      setState({
        loading: false,
        error: '',
        run: detailResponse.data?.run,
        telemetry,
      });
    } catch (error) {
      if (!background) {
        setState((current) => ({
          ...current,
          loading: false,
          error: error?.response?.data?.message || 'Unable to load this Run.',
        }));
      }
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isRunActive(state.run)) return undefined;
    const timer = window.setInterval(() => load({ background: true }), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load, state.run]);

  const handleDownload = async (file) => {
    setDownloading(file.file_id);
    setDownloadError('');
    try {
      const response = await axiosInstance.post(`/runs/${encodeURIComponent(runId)}/files/${encodeURIComponent(file.file_id)}/download`);
      const url = response.data?.download_url || response.data?.url;
      if (!url) throw new Error('Download URL was not returned.');
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = response.data?.filename || file.file_name || '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setDownloadError(error?.response?.data?.message || error?.message || 'Unable to download this Run file.');
    } finally {
      setDownloading('');
    }
  };

  const handleReissue = async () => {
    setCommandAction({ busy: true, error: '', success: '' });
    try {
      const response = await axiosInstance.post(`/runs/${encodeURIComponent(runId)}/command/reissue`);
      const status = response.data?.status;
      if (status === 'failed_to_dispatch' || status === 'validation_failed') {
        setCommandAction({
          busy: false,
          error: response.data?.error || 'AMX could not re-issue the command.',
          success: '',
        });
      } else {
        setCommandAction({ busy: false, error: '', success: 'Command re-issued; waiting for Asset acknowledgement.' });
      }
      await load({ background: true });
    } catch (error) {
      setCommandAction({
        busy: false,
        error: error?.response?.data?.message || error?.message || 'Unable to re-issue the command.',
        success: '',
      });
    }
  };

  const run = state.run;
  const progress = run?.progress ?? run?.progress_percent;
  const files = Array.isArray(run?.files) ? run.files : [];
  const assetStatus = run?.asset_status || {};
  const capabilityRequest = run?.capability_request || {};
  const command = run?.command || null;
  const commandAttempts = Array.isArray(command?.attempts) ? command.attempts : [];
  const commandArtifacts = Array.isArray(command?.artifacts) ? command.artifacts : [];
  const runEvents = Array.isArray(run?.run_events) ? run.run_events : [];
  const commandStatus = String(command?.status || '').toLowerCase();
  const canReissue = (
    (!run?.capability_request_id && ['rejected', 'ack_timeout', 'failed_to_dispatch', 'validation_failed'].includes(commandStatus))
    || (Boolean(run?.capability_request_id) && commandStatus === 'ack_timeout')
  );

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header breadcrumbItems={[
            { label: 'AMX xConsole', href: '/' },
            { label: run?.asset?.asset_name || 'Asset', href: run?.asset_id ? `/assets/${run.asset_id}` : '/assets' },
            { label: `Run ${String(runId).slice(0, 8)}` },
          ]} />
          {state.loading ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 8 }}><CircularProgress /><Typography>Loading Run...</Typography></Stack>
          ) : state.error ? <Alert severity="error">{state.error}</Alert> : run ? (
            <Stack spacing={2.5}>
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                  <Stack spacing={1}>
                    <Typography variant="overline" color="text.secondary">Asset Run</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 850 }}>{run.asset?.asset_name || 'Run'}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{run.run_id}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip color={runStatusColor(run.status)} label={label(run.status)} />
                      <Chip variant="outlined" label={`Trigger: ${label(run.trigger_source)}`} />
                      {isRunActive(run) ? <Chip color="success" variant="outlined" label="Live" /> : null}
                    </Stack>
                    {progress !== null && progress !== undefined ? (
                      <Box sx={{ width: { xs: '100%', md: 440 }, pt: 0.5 }}>
                        <Stack direction="row" justifyContent="space-between"><Typography variant="caption">Progress</Typography><Typography variant="caption">{Math.round(Number(progress))}%</Typography></Stack>
                        <LinearProgress variant="determinate" value={Number(progress)} sx={{ mt: 0.5, borderRadius: 1 }} />
                      </Box>
                    ) : null}
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignSelf={{ md: 'center' }}>
                    <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(-1)}>Back</Button>
                    {canReissue ? (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={commandAction.busy ? <CircularProgress size={16} /> : <ReplayRoundedIcon />}
                        onClick={handleReissue}
                        disabled={commandAction.busy}
                      >
                        {commandAction.busy ? 'Re-Issuing…' : 'Re-Issue Command'}
                      </Button>
                    ) : null}
                    <Button variant="contained" startIcon={<RefreshRoundedIcon />} onClick={() => load()}>Refresh</Button>
                  </Stack>
                </Stack>
              </Paper>
              <Paper variant="outlined"><Tabs value={tab} onChange={(_event, value) => setTab(value)} variant="scrollable"><Tab value="overview" label="Overview" /><Tab value="telemetry" label="Telemetry" /><Tab value="files" label={`Files (${files.length})`} /><Tab value="context" label="Shared With / Context" /></Tabs></Paper>
              {tab === 'overview' ? (
                <Stack spacing={2}>
                  {commandAction.error ? <Alert severity="error">{commandAction.error}</Alert> : null}
                  {commandAction.success ? <Alert severity="success">{commandAction.success}</Alert> : null}
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Table size="small"><TableBody>
                      {[
                        ['Run ID', run.run_id], ['Asset', run.asset?.asset_name], ['Status', label(run.status)],
                        ['Progress', progress === null || progress === undefined ? 'N/A' : `${Math.round(Number(progress))}%`],
                        ['Started', date(run.started_at)], ['Ended', date(run.ended_at || run.completed_at)],
                        ['Duration', formatRunDuration(run.duration_seconds)], ['Trigger Source', label(run.trigger_source)],
                        ['Triggered By', run.trigger_user?.full_name || run.trigger_user?.email || run.trigger_user_id || 'N/A'],
                        ['Capability Request', runContextLabel(run)], ['Command', command?.display_name || command?.operation || command?.command_type || run.command_id || 'N/A'],
                      ].map(([key, value]) => <TableRow key={key}><TableCell sx={{ width: 230, fontWeight: 750 }}>{key}</TableCell><TableCell sx={{ wordBreak: 'break-word' }}>{value}</TableCell></TableRow>)}
                    </TableBody></Table>
                  </Paper>
                  {command ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Command</Typography>
                      <Table size="small"><TableBody>
                        {[
                          ['Name', command.display_name || command.command_type],
                          ['Operation', command.operation || command.command_type],
                          ['Command ID', command.command_id],
                          ['Delivery Status', label(command.status)],
                          ['Triggered By', run.trigger_user?.full_name || run.trigger_user?.email || run.trigger_user_id || 'N/A'],
                        ].map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell sx={{ width: 230, fontWeight: 750 }}>{key}</TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>{value || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody></Table>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 2, mb: 1 }}>Parameters</Typography>
                      <JsonBlock value={command.parameters} emptyMessage="This command has no parameters." />
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 2, mb: 1 }}>Input Artifacts</Typography>
                      {commandArtifacts.length ? (
                        <Table size="small">
                          <TableHead><TableRow><TableCell>Artifact</TableCell><TableCell>Media Type</TableCell><TableCell>Size</TableCell></TableRow></TableHead>
                          <TableBody>{commandArtifacts.map((artifact) => (
                            <TableRow key={artifact.artifact_id}>
                              <TableCell>{artifact.filename}</TableCell>
                              <TableCell>{artifact.content_type || 'N/A'}</TableCell>
                              <TableCell>{bytes(artifact.size_bytes)}</TableCell>
                            </TableRow>
                          ))}</TableBody>
                        </Table>
                      ) : <Typography variant="body2" color="text.secondary">No input artifacts were attached.</Typography>}
                    </Paper>
                  ) : null}
                  {command ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>Command Delivery</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        MQTT publish attempts and connector acknowledgements. A timeout means delivery is unknown, not that execution failed.
                      </Typography>
                      <Table size="small">
                        <TableHead><TableRow><TableCell>Attempt</TableCell><TableCell>Status</TableCell><TableCell>Sent</TableCell><TableCell>Acknowledged / Deadline</TableCell><TableCell>Message</TableCell></TableRow></TableHead>
                        <TableBody>
                          {commandAttempts.length ? commandAttempts.map((attempt) => (
                            <TableRow key={attempt.attempt_id}>
                              <TableCell>{attempt.attempt_number}</TableCell>
                              <TableCell><Chip size="small" color={deliveryColor(attempt.status)} label={label(attempt.status)} /></TableCell>
                              <TableCell>{date(attempt.published_at || attempt.issued_at)}</TableCell>
                              <TableCell>{date(attempt.ack_received_at || attempt.ack_deadline_at)}</TableCell>
                              <TableCell>{attempt.ack_message || attempt.publish_error || '—'}</TableCell>
                            </TableRow>
                          )) : <TableRow><TableCell colSpan={5}>No delivery attempts were recorded.</TableCell></TableRow>}
                        </TableBody>
                      </Table>
                    </Paper>
                  ) : null}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>Run Events</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Asset-reported execution lifecycle, separate from command delivery.
                    </Typography>
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Event</TableCell><TableCell>Result</TableCell><TableCell>Progress</TableCell></TableRow></TableHead>
                      <TableBody>
                        {runEvents.length ? runEvents.map((event) => (
                          <TableRow key={event.event_id}>
                            <TableCell>{date(event.event_ts)}</TableCell>
                            <TableCell>{label(event.event_type)}</TableCell>
                            <TableCell>{label(event.processing_result)}</TableCell>
                            <TableCell>{event.payload?.progress === null || event.payload?.progress === undefined ? '—' : `${Math.round(Number(event.payload.progress))}%`}</TableCell>
                          </TableRow>
                        )) : <TableRow><TableCell colSpan={4}>The Asset has not published any Run events yet.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Current Asset Status</Typography>
                    <Table size="small"><TableBody>
                      {[
                        ['Connection', assetStatus.connection_status], ['Operational', assetStatus.operational_status],
                        ['Availability', assetStatus.availability_status], ['Health', assetStatus.health_status],
                        ['Activity', assetStatus.activity], ['Mode', assetStatus.mode],
                        ['Status Message', assetStatus.status_message], ['Observed', date(assetStatus.observed_at)],
                        ['Error', assetStatus.error_message || assetStatus.error_code],
                      ].map(([key, value]) => <TableRow key={key}><TableCell sx={{ width: 230, fontWeight: 750 }}>{key}</TableCell><TableCell>{value || 'N/A'}</TableCell></TableRow>)}
                    </TableBody></Table>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Execution Metadata</Typography>
                    <JsonBlock value={run.metadata} emptyMessage="No execution metadata was recorded for this Run." />
                    {run.command ? (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Command Context</Typography>
                        <JsonBlock
                          value={{
                            command_id: run.command.command_id,
                            command_type: run.command.command_type,
                            status: run.command.status,
                            request_payload: run.command.request_payload,
                            command_payload: run.command.command_payload,
                            response_payload: run.command.response_payload,
                          }}
                          emptyMessage="No command context was recorded."
                        />
                      </Box>
                    ) : null}
                  </Paper>
                </Stack>
              ) : null}
              {tab === 'telemetry' ? <RunTelemetryPanel telemetry={state.telemetry} /> : null}
              {tab === 'files' ? (
                <Stack spacing={1.5}>
                  {downloadError ? <Alert severity="error">{downloadError}</Alert> : null}
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Table size="small"><TableHead><TableRow><TableCell>File</TableCell><TableCell>Size</TableCell><TableCell>Created</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead><TableBody>
                      {files.length ? files.map((file) => <TableRow key={file.file_id}><TableCell sx={{ fontWeight: 750 }}>{file.file_name}</TableCell><TableCell>{bytes(file.size_bytes)}</TableCell><TableCell>{date(file.created_at)}</TableCell><TableCell align="right"><Button size="small" startIcon={<DownloadRoundedIcon />} onClick={() => handleDownload(file)} disabled={downloading === file.file_id}>{downloading === file.file_id ? 'Preparing...' : 'Download'}</Button></TableCell></TableRow>) : <TableRow><TableCell colSpan={4}>No files or artifacts are associated with this Run.</TableCell></TableRow>}
                    </TableBody></Table>
                  </Paper>
                </Stack>
              ) : null}
              {tab === 'context' ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Asset Owner</Typography>
                    <Typography variant="body2">{run.asset?.business_unit_display_name || run.asset?.business_unit_id}</Typography>
                    <Typography variant="caption" color="text.secondary">{run.asset?.partner_display_name || run.asset?.partner_id}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {run.capability_request ? (
                      <Stack spacing={0.75}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{capabilityRequest.capability_name || 'Capability Request'}</Typography>
                        <Typography variant="body2">{capabilityRequest.request_title}</Typography>
                        <Typography variant="caption" color="text.secondary">Capability Request {capabilityRequest.capability_request_id}</Typography>
                        <Typography variant="body2">Requester Business Unit: {capabilityRequest.requesting_business_unit_id || 'N/A'}</Typography>
                        <Typography variant="body2">Provider Business Unit: {capabilityRequest.provider_business_unit_id || 'N/A'}</Typography>
                        {run.workspace ? <Typography variant="body2">Workspace: {run.workspace.title || run.workspace.workspace_id}</Typography> : null}
                      </Stack>
                    ) : <Typography variant="body2" color="text.secondary">This Run has not been shared through a Capability Request or Workspace.</Typography>}
                  </Paper>
                </Stack>
              ) : null}
            </Stack>
          ) : null}
        </Box>
      </Box>
    </AppTheme>
  );
}
